import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferOutput } from "@trpc/tanstack-react-query";
import { z } from "zod";
import { queryClient, trpc } from "~/utils/trpc";
import LoginPage from "~/pages/LoginPage";
import { Alert, AlertDescription } from "@quake/shared-ui/components/ui/alert";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import CheckIcon from "lucide-react/dist/esm/icons/check";
import PlugZapIcon from "lucide-react/dist/esm/icons/plug-zap";
import XIcon from "lucide-react/dist/esm/icons/x";
import { redirectWithReload } from "~/utils/redirect-with-reload";
import {
  buildOAuthCallbackUrl,
  isMcpOAuthRequest,
  OAUTH_AUTHORIZATION_UNAVAILABLE_MESSAGE,
} from "~/utils/openapi-oauth";

const openApiOauthSearchSchema = z
  .object({
    client_id: z.string(),
    redirect_uri: z.url().optional(),
    response_type: z.literal("code"),
    scope: z.string().optional(),
    state: z.string().optional(),
    channel: z.enum(["chrome_extension"]).optional(),
    code_challenge_method: z.enum(["S256"]).optional(),
    code_challenge: z.string().min(43).max(128).optional(),
    resource: z.string().url().optional(),
    authorization_error: z.literal("unavailable").optional(),
  })
  .superRefine((value, ctx) => {
    if (value.channel !== "chrome_extension" && !value.redirect_uri) {
      ctx.addIssue({
        code: "custom",
        path: ["redirect_uri"],
        message: "redirect_uri is required",
      });
    }
    if (value.channel === "chrome_extension" && !value.code_challenge) {
      ctx.addIssue({
        code: "custom",
        path: ["code_challenge"],
        message: "code_challenge is required",
      });
    }
  });

export const Route = createFileRoute("/_public/openapi-oauth")({
  validateSearch: openApiOauthSearchSchema,
  component: RouteComponent,
});

type AuthResponse = inferOutput<typeof trpc.auth.handleGoogleCallback>;

const getScopeDescription = (scope: string) => {
  if (scope === "mcp:read")
    return "Read permitted Dataset data, discover actions, and inspect enabled integration diagnostics";
  if (scope === "mcp:write") return "Execute installed app actions and inspect their run status";
  return scope;
};

function RouteComponent() {
  const navigate = useNavigate();
  const {
    client_id,
    redirect_uri,
    response_type,
    scope,
    state,
    channel,
    code_challenge_method,
    code_challenge,
    resource,
    authorization_error: authorizationError,
  } = Route.useSearch();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const authorizeMutation = useMutation(trpc.openapiOauth.authorize.mutationOptions());
  const denyMutation = useMutation(trpc.openapiOauth.deny.mutationOptions());
  const { data: session } = useQuery(trpc.auth.getSession.queryOptions());
  const oauthRequest = {
    client_id,
    redirect_uri,
    response_type,
    scope,
    state,
    channel,
    code_challenge,
    code_challenge_method,
    resource,
  };
  const preparationQuery = useQuery(
    trpc.openapiOauth.prepareAuthorization.queryOptions(oauthRequest, {
      enabled: !authorizationError,
      retry: false,
    }),
  );
  const datasetId = session?.isLoggedIn && session.authStage === "authenticated" ? session.dataset?.id : undefined;
  const sessionDatasetId = session?.isLoggedIn ? session.dataset?.id : undefined;
  const requiredDatasetId = preparationQuery.data?.dataset_id ?? undefined;
  const requiresDatasetSelection = Boolean(
    session?.isLoggedIn && requiredDatasetId && sessionDatasetId !== requiredDatasetId,
  );
  const oauthInput = {
    ...oauthRequest,
    dataset_id: datasetId ?? "",
  };
  const isMcpAuthorization = preparationQuery.data?.kind === "mcp_public" || isMcpOAuthRequest(oauthInput);
  const consentQuery = useQuery(
    trpc.openapiOauth.resolveConsent.queryOptions(oauthInput, {
      enabled: Boolean(datasetId) && isMcpAuthorization,
    }),
  );

  const continueViaAuthorizePreflight = () => {
    const authorizeUrl = new URL("/api/openapi/authorize", window.location.origin);
    for (const [key, value] of Object.entries(oauthRequest)) {
      if (value !== undefined) authorizeUrl.searchParams.set(key, value);
    }
    redirectWithReload(authorizeUrl.toString());
  };

  useEffect(() => {
    if (authorizationError || preparationQuery.isPending || preparationQuery.error || !session?.isLoggedIn) return;

    if (requiresDatasetSelection || session.authStage === "first_factor") {
      continueViaAuthorizePreflight();
      return;
    }

    if (session.authStage === "mfa_pending") {
      navigate({ to: "/mfa", search: { oauth: oauthRequest }, replace: true });
    }
    // The OAuth request is immutable for this route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authorizationError,
    preparationQuery.isPending,
    preparationQuery.error,
    requiredDatasetId,
    requiresDatasetSelection,
    session,
  ]);

  useEffect(() => {
    if (
      !datasetId ||
      authorizationError ||
      preparationQuery.isPending ||
      preparationQuery.error ||
      requiresDatasetSelection ||
      isMcpAuthorization ||
      authorizeMutation.isPending ||
      authorizeMutation.isSuccess
    ) {
      return;
    }

    authorizeMutation
      .mutateAsync(oauthInput)
      .then((result) => redirectWithReload(buildOAuthCallbackUrl(result)))
      .catch((error) => setErrorMessage(error instanceof Error ? error.message : "Failed to complete OAuth flow."));
    // The OAuth request is immutable for this route; a dataset transition is the only trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId, authorizationError, preparationQuery.isPending, preparationQuery.error, requiresDatasetSelection]);

  const redirectToClient = (result: { redirect_uri: string; state?: string; code?: string }, denied = false) => {
    const url = new URL(result.redirect_uri);
    if (denied) {
      url.searchParams.set("error", "access_denied");
      url.searchParams.set("error_description", "The user denied the request");
    } else if (result.code) {
      url.searchParams.set("code", result.code);
    }
    if (result.state) url.searchParams.set("state", result.state);
    redirectWithReload(url.toString());
  };

  const handleAuthSuccess = async (data: AuthResponse) => {
    let requiredOAuthDatasetId: string | undefined;
    try {
      const prepared = await queryClient.ensureQueryData(
        trpc.openapiOauth.prepareAuthorization.queryOptions(oauthRequest, { retry: false }),
      );
      requiredOAuthDatasetId = prepared.dataset_id ?? undefined;
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Invalid OAuth request.");
      return;
    }

    if (requiredOAuthDatasetId) {
      continueViaAuthorizePreflight();
      return;
    }

    if ("datasets" in data && data.datasets?.length) {
      navigate({
        to: "/select-dataset",
        search: {
          datasets: data.datasets,
          oauth: oauthRequest,
        },
      });
      return;
    }

    if (data.redirectPath === "/mfa") {
      navigate({
        to: "/mfa",
        search: {
          oauth: oauthRequest,
        },
      });
      return;
    }

    await queryClient.invalidateQueries({ queryKey: trpc.auth.getSession.queryKey() });
  };

  const visibleError = authorizationError
    ? OAUTH_AUTHORIZATION_UNAVAILABLE_MESSAGE
    : (errorMessage ?? preparationQuery.error?.message);

  if (authorizationError) {
    return (
      <div className="w-full max-w-md">
        <Alert className="border-destructive bg-destructive/10">
          <AlertDescription>{OAUTH_AUTHORIZATION_UNAVAILABLE_MESSAGE}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (datasetId && !isMcpAuthorization) {
    return (
      <div className="w-full max-w-md">
        {visibleError ? (
          <Alert className="border-destructive bg-destructive/10">
            <AlertDescription>{visibleError}</AlertDescription>
          </Alert>
        ) : (
          <p className="text-center text-sm text-muted-foreground">Completing OAuth authorization...</p>
        )}
      </div>
    );
  }

  if (datasetId && isMcpAuthorization) {
    const consent = consentQuery.data;
    const isMcpConsent = consent?.requested_scopes.some((requestedScope) => requestedScope.startsWith("mcp:")) ?? false;
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md border bg-muted">
            <PlugZapIcon className="h-4 w-4" />
          </div>
          <div>
            <CardTitle className="text-lg">Connect {consent?.client.name ?? "OAuth client"}</CardTitle>
            <CardDescription className="mt-1">
              Review access to {consent?.dataset.name ?? "this Dataset"}.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {consentQuery.error ? (
            <Alert className="border-destructive bg-destructive/10">
              <AlertDescription>{consentQuery.error.message}</AlertDescription>
            </Alert>
          ) : null}
          <div className="space-y-2 border-y py-3">
            {consent?.requested_scopes.map((requestedScope) => {
              const granted = consent.granted_scopes.includes(requestedScope);
              const ScopeIcon = granted ? CheckIcon : XIcon;
              return (
                <div className="flex gap-2 text-sm" key={requestedScope}>
                  <ScopeIcon
                    className={granted ? "mt-0.5 h-4 w-4 text-emerald-600" : "mt-0.5 h-4 w-4 text-muted-foreground"}
                  />
                  <div>
                    <div>{getScopeDescription(requestedScope)}</div>
                    {!granted ? (
                      <div className="text-xs text-muted-foreground">Not granted by your current role</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            {isMcpConsent
              ? `Data access and actions remain limited by your current permissions in ${consent?.dataset.name ?? "this Dataset"}.`
              : "Only the access listed above will be granted to this client."}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              disabled={!consent || denyMutation.isPending || authorizeMutation.isPending}
              onClick={async () => {
                try {
                  redirectToClient(await denyMutation.mutateAsync(oauthInput), true);
                } catch (error) {
                  setErrorMessage(error instanceof Error ? error.message : "Failed to deny authorization.");
                }
              }}
            >
              Deny
            </Button>
            <Button
              disabled={!consent || denyMutation.isPending || authorizeMutation.isPending}
              onClick={async () => {
                try {
                  redirectToClient(await authorizeMutation.mutateAsync(oauthInput));
                } catch (error) {
                  setErrorMessage(error instanceof Error ? error.message : "Failed to approve authorization.");
                }
              }}
            >
              Allow
            </Button>
          </div>
          {errorMessage ? (
            <Alert className="border-destructive bg-destructive/10">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full">
      {visibleError ? (
        <Alert className="bg-destructive/10 border-destructive mb-4">
          <AlertDescription>{visibleError}</AlertDescription>
        </Alert>
      ) : null}
      <LoginPage onAuthSuccess={handleAuthSuccess} oauthContext={oauthRequest} />
    </div>
  );
}
